import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";

type DialogMode = "create" | "variant";

interface SaveTemplateDialogProps {
  open: boolean;
  sourceName: string;
  bindingCount: number;
  saving: boolean;
  mode?: DialogMode;
  initialName?: string;
  initialDocumentLevelType?: string;
  initialNotes?: string;
  onClose: () => void;
  onSave: (input: {
    name: string;
    documentLevelType: string;
    notes: string;
    alsoCreateEntities: boolean;
  }) => void;
}

export default function SaveTemplateDialog({
  open,
  sourceName,
  bindingCount,
  saving,
  mode = "create",
  initialName = "",
  initialDocumentLevelType = "",
  initialNotes = "",
  onClose,
  onSave,
}: SaveTemplateDialogProps) {
  const [name, setName] = useState(initialName);
  const [documentLevelType, setDocumentLevelType] = useState(
    initialDocumentLevelType,
  );
  const [notes, setNotes] = useState(initialNotes);
  const [alsoCreateEntities, setAlsoCreateEntities] = useState(false);

  // Repopulate when the dialog opens or initial values change (variant mode
  // gets new values each time it's opened for a different applied template).
  useEffect(() => {
    if (open) {
      setName(initialName);
      setDocumentLevelType(initialDocumentLevelType);
      setNotes(initialNotes);
      setAlsoCreateEntities(false);
    }
  }, [open, initialName, initialDocumentLevelType, initialNotes]);

  const handleSubmit = () => {
    if (!name.trim() || !documentLevelType.trim()) return;
    onSave({
      name: name.trim(),
      documentLevelType: documentLevelType.trim(),
      notes,
      alsoCreateEntities,
    });
  };

  const isVariant = mode === "variant";
  const title = isVariant ? "Save as New Template" : "Save Template";
  const submitLabel = isVariant ? "Save Variant" : "Save Template";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ marginTop: 1 }}>
          <TextField
            label="Template name"
            placeholder="e.g. Aetna EOB v1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Source"
            value={sourceName}
            disabled
            fullWidth
            helperText={
              isVariant
                ? "Inherited from the original template"
                : "Set on the Map tab"
            }
          />
          <TextField
            label="Document level type"
            placeholder="e.g. EOP_PDF, EOB_DPPO"
            value={documentLevelType}
            onChange={(e) => setDocumentLevelType(e.target.value)}
            required
            fullWidth
            helperText="UPPERCASE identifier — match an existing llm_config.document_level_type or coordinate with engineering"
          />
          <TextField
            label="Description (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Field bindings"
            value={`${bindingCount} field${bindingCount === 1 ? "" : "s"} bound`}
            disabled
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={alsoCreateEntities}
                onChange={(e) => setAlsoCreateEntities(e.target.checked)}
              />
            }
            label="Also create entities from this document"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name.trim() || !documentLevelType.trim() || saving}
        >
          {saving ? "Saving…" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
